import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowRight } from "lucide-react";
import { useSite, scrollToId } from "@/lib/site";
import { EASE } from "./Reveal";
import { HeroVisual } from "./HeroVisual";

const Line = ({ children, delay }) => (
  <span className="block overflow-hidden pb-[0.06em] pt-[0.04em]">
    <motion.span className="brand-text block" initial={{ y: "110%" }} animate={{ y: "0%" }} transition={{ duration: 1.1, delay, ease: EASE }}>
      {children}
    </motion.span>
  </span>
);

const FadeUp = ({ children, delay, className }) => (
  <motion.div className={className} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay, ease: EASE }}>
    {children}
  </motion.div>
);

export const Hero = () => {
  const { t } = useSite();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yVisual = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const yGlow = useTransform(scrollYProgress, [0, 1], [0, -160]);

  return (
    <section id="top" ref={ref} className="relative overflow-hidden pb-24 pt-32 sm:pt-40 lg:min-h-[100svh] lg:pb-32" data-testid="hero-section">
      <div className="grid-bg pointer-events-none absolute inset-0" />
      <motion.div style={{ y: yGlow }} className="pointer-events-none absolute inset-0">
        <div className="orb absolute -left-32 top-10 h-[420px] w-[420px] rounded-full bg-[#8B5CF6]/25 blur-[120px]" />
        <div className="orb absolute -right-20 top-1/3 h-[360px] w-[360px] rounded-full bg-[#6D28D9]/25 blur-[120px]" style={{ animationDelay: "-6s" }} />
      </motion.div>

      <div className="relative mx-auto grid max-w-7xl items-center gap-16 px-5 sm:px-8 lg:grid-cols-12 lg:gap-8">
        <div className="lg:col-span-7">
          <FadeUp delay={0.1} className="mb-8 inline-flex items-center gap-2 rounded-full border border-[var(--line)] px-3 py-1.5 font-mono text-[11px] uppercase tracking-[0.2em] text-[var(--muted)] sm:text-xs">
            <span className="h-1.5 w-1.5 rounded-full bg-[#8B5CF6]" /> {t.hero.eyebrow}
          </FadeUp>
          <h1 className="font-display text-[12.5vw] font-black uppercase leading-[0.86] tracking-tighter brand-glow lg:text-[min(8vw,7.4rem)]" data-testid="hero-title">
            <Line delay={0.2}>Resilience</Line>
            <Line delay={0.36}>Brothers</Line>
          </h1>
          <FadeUp delay={0.7} className="mt-8 max-w-xl">
            <p className="font-display text-lg font-semibold text-[var(--text)] md:text-xl" data-testid="hero-subtitle">{t.hero.subtitle}</p>
            <p className="mt-3 text-sm leading-relaxed text-[var(--muted)] sm:text-base">{t.hero.body}</p>
          </FadeUp>
          <FadeUp delay={0.85} className="mt-10 flex flex-wrap gap-3">
            <button onClick={() => scrollToId("proyectos")} data-testid="hero-explore-btn" className="pill-primary group inline-flex items-center gap-2 rounded-full px-6 py-3.5 text-sm font-semibold">
              {t.hero.primary} <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </button>
            <button onClick={() => scrollToId("contacto")} data-testid="hero-contact-btn" className="pill-outline inline-flex items-center gap-2 rounded-full px-6 py-3.5 text-sm font-semibold">
              {t.hero.secondary}
            </button>
          </FadeUp>
        </div>
        <div className="lg:col-span-5">
          <HeroVisual label={t.hero.card} count={t.projects.items.length} scrollY={yVisual} />
        </div>
      </div>

      <FadeUp delay={1.2} className="absolute bottom-8 left-1/2 hidden -translate-x-1/2 flex-col items-center gap-2 font-mono text-[11px] uppercase tracking-[0.3em] text-[var(--faint)] lg:flex">
        {t.hero.scroll}
        <ArrowDown className="h-4 w-4 animate-bounce" />
      </FadeUp>
    </section>
  );
};
